import * as THREE from 'three'
import * as OBF from '@thatopen/components-front'

// Imperative facade for measurement controls. useMeasure registers the
// LengthMeasurement / AreaMeasurement instances on mount; the ribbon
// imports this module to drive them without holding a ref into the
// canvas component tree (mirrors the viewerApi pattern).

let length: OBF.LengthMeasurement | null = null
let area: OBF.AreaMeasurement | null = null
let totalCount = 0
const listeners = new Set<() => void>()

interface VpMeasurementInput {
  type: 'length' | 'area'
  start?: { x: number; y: number; z: number }
  end?: { x: number; y: number; z: number }
  points?: { x: number; y: number; z: number }[]
}

function notify() {
  for (const fn of listeners) fn()
}

export const measureApi = {
  setLength(c: OBF.LengthMeasurement | null) {
    length = c
  },
  setArea(c: OBF.AreaMeasurement | null) {
    area = c
  },
  hasTools(): boolean {
    return length !== null && area !== null
  },
  // ThatOpen tools accumulate measurements in their internal `list`.
  // This wipes everything (lines, fills, labels included) and resets
  // the count, used by the ribbon "Limpar" button.
  clearAll() {
    if (length) {
      length.cancelCreation()
      length.list.clear()
      length.lines.clear()
      length.labels.clear()
    }
    if (area) {
      area.cancelCreation()
      area.list.clear()
      area.lines.clear()
      area.fills.clear()
      area.labels.clear()
    }
    setTotalCount(0)
  },
  cancelInProgress() {
    length?.cancelCreation()
    area?.cancelCreation()
  },
  getCount(): number {
    return totalCount
  },
  // Returns a snapshot of the live tool instances so callers (e.g.
  // viewpoint capture) can iterate `list` without poking at module
  // internals. Either field may be null if the world hasn't bootstrapped.
  getTools(): {
    length: OBF.LengthMeasurement | null
    area: OBF.AreaMeasurement | null
  } {
    return { length, area }
  },
  // Restores a list of measurements captured by a viewpoint. We need
  // the same hs/Hi constructors that ThatOpen uses internally, which
  // aren't exported; grab them off the live `_temp` stash. Falls back
  // gracefully if the tool hasn't been wired up yet.
  restoreMeasurements(items: VpMeasurementInput[]): void {
    if (!length || !area) return
    const lengthTool = length as unknown as {
      _temp: { line: { constructor: new () => THREE.Line3 } }
      list: { add: (line: THREE.Line3) => void }
    }
    const areaTool = area as unknown as {
      _temp: {
        area: {
          constructor: new (pts?: THREE.Vector3[]) => {
            points: { add: (...pts: THREE.Vector3[]) => void }
          }
        }
      }
      list: {
        add: (a: {
          points: { add: (...pts: THREE.Vector3[]) => void }
        }) => void
      }
    }
    for (const item of items) {
      if (item.type === 'length' && item.start && item.end) {
        const Ctor = lengthTool._temp.line.constructor
        const line = new Ctor()
        line.start.set(item.start.x, item.start.y, item.start.z)
        line.end.set(item.end.x, item.end.y, item.end.z)
        lengthTool.list.add(line)
      } else if (item.type === 'area' && item.points && item.points.length >= 3) {
        const Ctor = areaTool._temp.area.constructor
        const a = new Ctor()
        a.points.add(
          ...item.points.map((p) => new THREE.Vector3(p.x, p.y, p.z)),
        )
        areaTool.list.add(a)
      }
    }
  },
  subscribe(fn: () => void): () => void {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  },
}

// useMeasure recomputes the count whenever a measurement's `list`
// fires onUpdated, then calls this so the ribbon can re-render.
export function setTotalCount(n: number) {
  totalCount = n
  notify()
}
